import React, { useState, useMemo } from 'react';
import { useAlerts } from '../contexts/AlertsContext';
import type { AlertType, AlertCategory } from '@financial-portfolio/shared/types';
import { AlertSettingsModal } from './AlertSettingsModal';

interface AlertsPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

const AlertsPanel: React.FC<AlertsPanelProps> = ({ isOpen, onClose }) => {
  const { alerts, unreadCount, markAsRead, markAllAsRead, dismissAlert, clearAlerts } = useAlerts();
  const [filterType, setFilterType] = useState<AlertType | 'all'>('all');
  const [filterCategory, setFilterCategory] = useState<AlertCategory | 'all'>('all');
  const [showSettings, setShowSettings] = useState(false);

  const filteredAlerts = useMemo(() => {
    return alerts.filter(alert => {
      if (filterType !== 'all' && alert.type !== filterType) return false;
      if (filterCategory !== 'all' && alert.category !== filterCategory) return false;
      return true;
    });
  }, [alerts, filterType, filterCategory]);

  const getAlertIcon = (type: AlertType) => {
    switch (type) {
      case 'success': return '✅';
      case 'info': return 'ℹ️';
      case 'warning': return '⚠️';
      case 'urgent': return '🚨';
      default: return '📢';
    }
  };

  const getAlertColor = (type: AlertType) => {
    switch (type) {
      case 'success': return 'bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-800';
      case 'info': return 'bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-800';
      case 'warning': return 'bg-yellow-50 dark:bg-yellow-900/30 border-yellow-200 dark:border-yellow-800';
      case 'urgent': return 'bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-800';
      default: return 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700';
    }
  };

  const getAlertBadgeColor = (type: AlertType) => {
    switch (type) {
      case 'success': return 'bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300';
      case 'info': return 'bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300';
      case 'warning': return 'bg-yellow-100 dark:bg-yellow-900/50 text-yellow-800 dark:text-yellow-300';
      case 'urgent': return 'bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-300';
      default: return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300';
    }
  };

  const formatTimestamp = (timestamp: Date) => {
    const now = new Date();
    const diffMs = now.getTime() - new Date(timestamp).getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return new Date(timestamp).toLocaleDateString('en-AU', {
      month: 'short',
      day: 'numeric',
      year: now.getFullYear() !== new Date(timestamp).getFullYear() ? 'numeric' : undefined
    });
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-30 z-40"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="fixed right-0 top-0 h-full w-full sm:w-[480px] bg-white dark:bg-gray-800 shadow-2xl z-50 flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-bold">Alerts</h2>
              {unreadCount > 0 && (
                <span className="bg-white text-blue-600 text-xs font-bold px-2 py-1 rounded-full">
                  {unreadCount} new
                </span>
              )}
            </div>
            <button
              onClick={onClose}
              className="text-white hover:bg-white hover:bg-opacity-20 rounded p-1 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Actions */}
          <div className="flex gap-2 text-sm flex-wrap">
            <button
              onClick={() => setShowSettings(true)}
              className="text-white hover:bg-white hover:bg-opacity-20 px-3 py-1 rounded transition-colors flex items-center gap-1"
              title="Alert Settings"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Settings
            </button>
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="text-white hover:bg-white hover:bg-opacity-20 px-3 py-1 rounded transition-colors"
              >
                Mark all read
              </button>
            )}
            {alerts.length > 0 && (
              <button
                onClick={() => {
                  if (confirm('Are you sure you want to clear all alerts?')) {
                    clearAlerts();
                  }
                }}
                className="text-white hover:bg-white hover:bg-opacity-20 px-3 py-1 rounded transition-colors"
              >
                Clear all
              </button>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="p-4 border-b border-gray-200 bg-gray-50">
          <div className="flex gap-2 flex-wrap">
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value as AlertType | 'all')}
              className="text-sm border border-gray-300 rounded px-2 py-1"
            >
              <option value="all">All Types</option>
              <option value="urgent">🚨 Urgent</option>
              <option value="warning">⚠️ Warning</option>
              <option value="info">ℹ️ Info</option>
              <option value="success">✅ Success</option>
            </select>

            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value as AlertCategory | 'all')}
              className="text-sm border border-gray-300 rounded px-2 py-1"
            >
              <option value="all">All Categories</option>
              <option value="property_value">Property Value</option>
              <option value="rental_yield">Rental Yield</option>
              <option value="cash_flow">Cash Flow</option>
              <option value="expenses">Expenses</option>
              <option value="market_news">Market News</option>
              <option value="general">General</option>
            </select>
          </div>
        </div>

        {/* Alerts List */}
        <div className="flex-1 overflow-y-auto">
          {filteredAlerts.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-500 p-8">
              <svg className="w-16 h-16 mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
              <p className="text-lg font-medium mb-2">No alerts</p>
              <p className="text-sm text-center">
                {filterType !== 'all' || filterCategory !== 'all'
                  ? 'No alerts match your filters'
                  : 'You\'re all caught up! Alerts will appear here when there are important changes.'}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {filteredAlerts.map(alert => (
                <div
                  key={alert.id}
                  className={`p-4 hover:bg-gray-50 transition-colors ${!alert.isRead ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                    }`}
                  onClick={() => !alert.isRead && markAsRead(alert.id)}
                >
                  <div className="flex items-start gap-3">
                    <span className="text-2xl flex-shrink-0">{getAlertIcon(alert.type)}</span>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <h3 className="font-semibold text-gray-900 text-sm">{alert.title}</h3>
                        <span className={`text-xs px-2 py-1 rounded-full flex-shrink-0 ${getAlertBadgeColor(alert.type)}`}>
                          {alert.type}
                        </span>
                      </div>

                      <p className="text-sm text-gray-700 mb-2">{alert.message}</p>

                      <div className="flex items-center justify-between text-xs text-gray-500">
                        <span>{formatTimestamp(alert.timestamp)}</span>

                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            dismissAlert(alert.id);
                          }}
                          className="text-gray-400 hover:text-red-600 transition-colors"
                        >
                          Dismiss
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Settings Modal */}
      <AlertSettingsModal
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
      />
    </>
  );
};

export default AlertsPanel;
